// app/Components/Plugins/Bots/BotMonitor.tsx
'use client'

import React, { useEffect, useRef, useState } from 'react'
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  UTCTimestamp,
  LineStyle,
  createSeriesMarkers
} from 'lightweight-charts'
import { Bot } from './types'
import BotForm from './BotForm'

// Candle bar shape
type Candle = {
  time: UTCTimestamp
  open: number
  high: number
  low: number
  close: number
  volume?: number
}

// Alert marker shape
type AlertMarker = {
  time: UTCTimestamp
  price: number
  text: string
  color: string
}

// Log entry shape
type LogEntry = {
  timestamp: string
  message: string
}

export default function BotMonitor() {
  // --- State ---
  const [bots, setBots] = useState<Bot[]>([])
  const [candles, setCandles] = useState<Candle[]>([])
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [alertMarkers, setAlertMarkers] = useState<AlertMarker[]>([])
  const [rsiData, setRsiData] = useState<{ time: UTCTimestamp; value: number }[]>([])
  const [showRSI, setShowRSI] = useState(false)
  const [showBotForm, setShowBotForm] = useState(false)
  const [selectedSymbol, setSelectedSymbol] = useState('BTCUSDT')
  const [availableSymbols, setAvailableSymbols] = useState<string[]>(['BTCUSDT'])

  // --- Refs ---
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<any>(null)
  const candleSeriesRef = useRef<any>(null)
  const priceLinesRef = useRef<any[]>([])

  const rsiChartContainerRef = useRef<HTMLDivElement>(null)
  const rsiChartRef = useRef<any>(null)
  const rsiLineSeriesRef = useRef<any>(null)
  const rsiPriceLinesRef = useRef<any[]>([])

  const priceHistoryRef = useRef<number[]>([])
  const prevPriceRef = useRef<number | null>(null)

  // --- Helpers ---
  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    setLogs(prev => [{ timestamp, message }, ...prev])
  }

  const addAlertMarker = (
    time: UTCTimestamp,
    price: number,
    text: string,
    color: string
  ) => {
    setAlertMarkers(prev => [...prev, { time, price, text, color }])
  }

  // --- Bots API ---
  const fetchBots = async () => {
    try {
      const res = await fetch('/api/bots')
      const data: Bot[] = await res.json()
      setBots(data)
      
      // Extract unique symbols from bots
      const symbols = Array.from(new Set(data.map(bot => bot.symbol)))
      if (symbols.length > 0) {
        setAvailableSymbols(symbols)
        // If current selected symbol is not in the list, switch to first available
        if (!symbols.includes(selectedSymbol)) {
          setSelectedSymbol(symbols[0])
        }
      }
      
      setShowRSI(data.some(bot => bot.conditions.some(c => c.indicator === 'rsi')))
    } catch (e) {
      console.error('Error fetching bots:', e)
    }
  }

  const handleBotCreated = () => {
    setShowBotForm(false)
    fetchBots()
  }

  const toggleBot = async (bot: Bot) => {
    await fetch('/api/bots', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...bot, active: !bot.active })
    })
    fetchBots()
  }

  // --- Historical Data ---
  const fetchHistoricalData = async (symbol: string) => {
    try {
      const res = await fetch(`https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=1m&limit=1000`)
      const data = await res.json()
      const bars: Candle[] = data.map((k: any) => ({
        time: k[0] / 1000,
        open: +k[1], high: +k[2], low: +k[3], close: +k[4], volume: +k[5]
      }))
      setCandles(bars)
      priceHistoryRef.current = bars.map(b => b.close)
      candleSeriesRef.current?.setData(bars)
    } catch (e) {
      console.error('Error fetching history:', e)
    }
  }

  // --- Threshold Lines ---
  const drawThresholdLines = () => {
    priceLinesRef.current.forEach(line => candleSeriesRef.current.removePriceLine(line))
    priceLinesRef.current = []
    bots.forEach(bot => {
      if (!bot.active || bot.symbol !== selectedSymbol) return
      bot.conditions.forEach(cond => {
        if (cond.indicator === 'price') {
          const line = candleSeriesRef.current.createPriceLine({
            price: cond.value,
            color: '#00FF00',
            lineWidth: 1,
            lineStyle: LineStyle.Dotted,
            axisLabelVisible: true,
            title: `${bot.botName} threshold`
          })
          priceLinesRef.current.push(line)
        }
      })
    })
  }

  const drawRSIThresholdLines = () => {
    rsiPriceLinesRef.current.forEach(line => rsiLineSeriesRef.current.removePriceLine(line))
    rsiPriceLinesRef.current = []
    bots.forEach(bot => {
      if (!bot.active || bot.symbol !== selectedSymbol) return
      bot.conditions.forEach(cond => {
        if (cond.indicator === 'rsi') {
          const line = rsiLineSeriesRef.current.createPriceLine({
            price: cond.value,
            color: '#00FFFF',
            lineWidth: 1,
            lineStyle: LineStyle.Dotted,
            axisLabelVisible: true,
            title: `${bot.botName} RSI threshold`
          })
          rsiPriceLinesRef.current.push(line)
        }
      })
    })
  }

  // --- Effects ---
  // 1) init charts & data
  useEffect(() => {
    if (!chartContainerRef.current) return
    chartRef.current = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 400,
      layout: { background: { color: '#00000000' }, textColor: '#88a1ac' },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      timeScale: { borderColor: '#485c7b' }
    })
    candleSeriesRef.current = chartRef.current.addSeries(CandlestickSeries, {})
    fetchBots()
    return () => chartRef.current.remove()
  }, [])

  // Effect to fetch historical data when symbol changes
  useEffect(() => {
    if (selectedSymbol && candleSeriesRef.current) {
      fetchHistoricalData(selectedSymbol)
      // Clear previous data
      setCandles([])
      setAlertMarkers([])
      setRsiData([])
      setLogs([])
    }
  }, [selectedSymbol])

  // 2) RSI pane
  useEffect(() => {
    if (showRSI && rsiChartContainerRef.current) {
      rsiChartRef.current = createChart(rsiChartContainerRef.current, {
        width: rsiChartContainerRef.current.clientWidth,
        height: 200,
        layout: { background: { color: '#000' }, textColor: '#d1d4dc' },
        timeScale: { borderColor: '#485c7b' }
      })
      rsiLineSeriesRef.current = rsiChartRef.current.addSeries(LineSeries, { color: '#FFA500', lineWidth: 2 })
    }
    return () => rsiChartRef.current?.remove()
  }, [showRSI])

  // 3) live data + server processing
  useEffect(() => {
    if (!selectedSymbol) return
    
    const ws = new WebSocket(`wss://stream.binance.com:9443/ws/${selectedSymbol.toLowerCase()}@kline_1m`)
    ws.onmessage = async ev => {
      const msg = JSON.parse(ev.data)
      if (msg.e !== 'kline') return
      const k = msg.k
      const newCandle: Candle = { time: Math.floor(k.t/1000) as UTCTimestamp, open:+k.o, high:+k.h, low:+k.l, close:+k.c, volume:+k.v }

      // update state & chart
      setCandles(prev => {
        const last = prev[prev.length-1]
        const updated = last?.time===newCandle.time ? [...prev.slice(0,-1), newCandle] : [...prev, newCandle]
        priceHistoryRef.current = updated.map(b => b.close)
        return updated
      })
      candleSeriesRef.current.update(newCandle)

      // call API for alerts
      if (prevPriceRef.current !== null) {
        const res = await fetch('/api/bots/runBots', {
          method: 'POST',
          headers: { 'Content-Type':'application/json' },
          body: JSON.stringify({ candle: newCandle, prevPrice: prevPriceRef.current, priceHistory: priceHistoryRef.current, symbol: selectedSymbol })
        })
        const { logs: newLogs, alertMarkers: newMarkers, rsiUpdates } = await res.json()
        newLogs.forEach((m: string) => addLog(m))
        newMarkers.forEach((a: { time: UTCTimestamp; price: number; text: string; color: string }) => addAlertMarker(a.time, a.price, a.text, a.color))
        rsiUpdates.forEach((r: { time: UTCTimestamp; value: number }) => setRsiData(prev=>[...prev,r]))
      }
      prevPriceRef.current = newCandle.close
    }
    ws.onerror = console.error
    ws.onclose = () => setTimeout(fetchBots,5000)
    return () => ws.close()
  }, [selectedSymbol, bots])

  // 4) markers on chart
  useEffect(() => {
    if (candleSeriesRef.current && alertMarkers.length) {
      const markers = alertMarkers.map(m => ({
        time: m.time,
        price: m.price,             // ← added
        position: 'aboveBar' as const,
        color: m.color,
        shape: 'arrowUp' as const,
        text: m.text
      }))
      createSeriesMarkers(candleSeriesRef.current, markers)
    }
  }, [alertMarkers])

  // 5) redraw thresholds
  useEffect(() => { drawThresholdLines(); drawRSIThresholdLines() }, [bots])

  // --- Render ---
  return (
    <div className="p-4 min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Bot Monitor — {selectedSymbol}</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm">Symbol:</label>
          <select 
            value={selectedSymbol} 
            onChange={(e) => setSelectedSymbol(e.target.value)}
            className="bg-[#88a1ac18] border border-gray-600 rounded px-2 py-1"
          >
            {availableSymbols.map(symbol => (
              <option key={symbol} value={symbol}>{symbol}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ display:'flex', gap:20 }}>
        <div ref={chartContainerRef} style={{ flex:2, height:400, border:'1px solid #88a1ac', borderRadius:10 }}/>
        <div style={{ flex:1, maxHeight:400, overflowY:'auto', border:'1px solid #88a1ac', padding:10, borderRadius:5, color:'#88a1ac' }}>
          <h3>Logs</h3>
          {logs.map((l,i)=><div key={i}><strong>{l.timestamp}:</strong> {l.message}</div>)}
        </div>
      </div>
      {showRSI && <><h3>RSI Chart</h3><div ref={rsiChartContainerRef} style={{ width:'100%', height:200, border:'1px solid #485c7b', borderRadius:12 }}/></>}
      <div style={{ marginTop:20, display:'flex', alignItems:'center', gap:10 }}>
        <h3>Active Bots ({selectedSymbol})</h3>
        <button onClick={()=>setShowBotForm(true)} className="px-2 py-1 bg-[#88a1ac49] rounded hover:bg-[#88a1ac89]">+ Add Bot</button>
      </div>
      {showBotForm && <BotForm onSuccess={handleBotCreated} onClose={() => setShowBotForm(false)} />}
      <table className="min-w-full border mt-4"><thead><tr><th className="border p-2">Name</th><th className="border p-2">Symbol</th><th className="border p-2">Exchange</th><th className="border p-2">Conditions</th><th className="border p-2">Triggers</th><th className="border p-2">Status</th><th className="border p-2">Actions</th></tr></thead><tbody>{bots.filter(bot => bot.symbol === selectedSymbol).map(b=> (<tr key={b.id}><td className="border p-2">{b.botName}</td><td className="border p-2">{b.symbol}</td><td className="border p-2">{b.exchange || 'binance'}</td><td className="border p-2">{b.conditions.map((c,i)=><div key={i}>{c.indicator} {c.type} {c.value} → {c.action}</div>)}</td><td className="border p-2">{b.triggerCount || 0}</td><td className="border p-2">{b.active?'Active':'Inactive'}</td><td className="border p-2"><button onClick={()=>toggleBot(b)} style={{ padding:'4px 8px', backgroundColor: b.active ? '#dc2626' : '#16a34a', color:'white', border:'none', borderRadius:4 }}>{b.active?'Deactivate':'Activate'}</button></td></tr>))}</tbody></table>
    </div>
  )
}
